/**
 * Shared types + helpers for email compose/reply flows. Used by the inbox
 * composer (ComposePanel) and the campaign reply composer (ReplyComposePanel)
 * so both agree on wire format, reply-all self-filtering, and mode naming.
 */

import { EmailRecipientOption } from './index'

/** Modes valid inside a thread reply (no standalone "New Message"). */
export type ReplyMode = 'reply' | 'reply_all' | 'forward'

/** All compose modes, including standalone "New Message". */
export type ComposeMode = ReplyMode | 'compose'

/** Recipient shape as sent/received on the backend wire. */
export interface WireRecipient {
  name: string
  email_address: string
}

/**
 * Parse a `{label, value}` option into `{name, email}`. Labels may be
 * `"Name <email>"` (from contact autocomplete), a bare email, or free-text
 * (label === value).
 */
export function parseEmailOption(option: EmailRecipientOption): {
  name: string
  email: string
} {
  const match = option.label.match(/^(.+?)\s*<(.+)>\s*$/)
  if (match) return { name: match[1].trim(), email: match[2].trim() }
  return { name: '', email: option.label || option.value }
}

/**
 * Permissive email format check: requires a non-empty local part, `@`, and a
 * domain with a dot. Good enough to reject obvious typos at chip-creation
 * time without trying to implement RFC 5322 — backend/EmailBison remains
 * the authoritative validator.
 */
export function looksLikeEmail(value: string): boolean {
  return /^\S+@\S+\.\S+$/.test(value.trim())
}

/**
 * Drop the workspace's own email from a list of contacts/recipients. Used to
 * build the reply-all Cc default without including the workspace sender.
 *
 * Generic so it works against both `EmailContact` (`.email`) and
 * `WireRecipient` (`.email_address`) shapes — caller supplies the accessor.
 */
export function filterSelf<T>(
  items: readonly T[],
  selfEmail: string | null | undefined,
  getEmail: (item: T) => string | null | undefined
): T[] {
  if (!selfEmail) return [...items]
  const self = selfEmail.toLowerCase()
  return items.filter((item) => {
    const email = getEmail(item)
    return email ? email.toLowerCase() !== self : true
  })
}

/**
 * Convert recipient-select options (as stored in chip state) to the
 * `{name, email_address}` wire shape the backend expects.
 */
export function toWireRecipients(
  options: readonly EmailRecipientOption[]
): WireRecipient[] {
  return options.map((opt) => {
    const { name, email } = parseEmailOption(opt)
    return { name, email_address: email }
  })
}

/**
 * Build the default Cc list for a "Reply All" action: original `to` + `cc`
 * recipients, deduplicated and with the workspace sender removed. Returns
 * `{label, value}` options ready to drop into recipient select state.
 */
export function buildReplyAllCcOptions(
  toEmails: readonly WireRecipient[],
  ccEmails: readonly WireRecipient[],
  selfEmail: string | null | undefined
): EmailRecipientOption[] {
  const filtered = filterSelf(
    [...toEmails, ...ccEmails],
    selfEmail,
    (r) => r.email_address
  )
  const seen = new Set<string>()
  const result: EmailRecipientOption[] = []
  for (const r of filtered) {
    const email = r.email_address
    if (!email) continue
    const key = email.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push({
      label: r.name ? `${r.name} <${email}>` : email,
      value: email,
    })
  }
  return result
}
